import { statusStore } from "@/stores/status";
import { THIRD_PARTY_MODEL_TYPES } from "@/thirdParty/constants";
import {
  AIMetaType,
  IGetImg2ImgMetaData,
  IGetTxt2ImgMetaData,
  Img2ImgMeta,
  Txt2ImgMeta,
} from "@/types/meta";
import {
  DREAMLIKE_VERSION,
  META_MODAL_NOLI_MODEL_MAP,
  NOLI_MODEL_META_MODAL_MAP,
} from "@/utils/constants";
import { parseLabels } from "@/utils/misc";
import { getRandomSeed } from "@noli/core";
import { isUndefined } from "lodash";
import { isNoliboxModel } from "../components/common/ModelSwitch/util";
import {
  DEFAULT_GUIDANCE_SCALE,
  DEFAULT_NUM_STEPS,
  ModelTypes,
  MODEL_TYPES_INFO,
  MODEL_META_PARAMS_CONFIG,
} from "../constant";
import { APIs_Img2Img } from "../request/api";

export function getTxt2ImgMetaData({
  lang,
  text,
  circular,
  modelType,
  displayNode,
  originalNegative,
  extraPromptInfo,
  txt2ImgCustomSize,
  customEmbeddings,
  highResInfo,
}: IGetTxt2ImgMetaData): Txt2ImgMeta["data"] {
  const { txt2imgStyle: style, txt2imgArtist: artist } = statusStore;
  const { w: txt2imgW, h: txt2imgH } =
    txt2ImgCustomSize ?? statusStore.getTxt2imgGenerateSize(displayNode);

  return {
    w: txt2imgW,
    h: txt2imgH,
    text: "",
    use_circular: circular,
    is_anime: modelType === "anime",
    seed: getRandomSeed(),
    model: isNoliboxModel(modelType) ? MODEL_TYPES_INFO[modelType].metaModel : undefined,
    promptInfo: {
      lang,
      original: text,
      originalNegative: originalNegative,
      style,
      artist,
      // TODO : add negative prompt here
      ...(extraPromptInfo ?? {}),
    },
    highres_info: highResInfo ?? undefined,
    custom_embeddings: customEmbeddings,
  };
}

export function getAdvanceModeMetaData({
  lang,
  text,
  circular,
  modelType,
  displayNode,
  originalNegative,
  extraPromptInfo,
  txt2ImgCustomSize,
  customEmbeddings,
  highResInfo,
}: IGetTxt2ImgMetaData): Txt2ImgMeta["data"] {
  const { w: txt2imgW, h: txt2imgH } =
    txt2ImgCustomSize ?? statusStore.getTxt2imgGenerateSize(displayNode);

  return {
    w: txt2imgW,
    h: txt2imgH,
    text: "",
    use_circular: circular,
    is_anime: modelType === "anime",
    seed: getRandomSeed(),
    model: NOLI_MODEL_META_MODAL_MAP[modelType],
    promptInfo: {
      lang,
      original: text,
      originalNegative: originalNegative,
      ...(extraPromptInfo ?? {}),
    },
    custom_embeddings: customEmbeddings,
    highres_info: highResInfo ?? undefined,
  };
}

export function getImg2ImgMetaData(
  props: IGetImg2ImgMetaData & { baseMetaFn: (props: IGetTxt2ImgMetaData) => Txt2ImgMeta["data"] },
): Img2ImgMeta["data"] {
  const { url, fidelity, baseMetaFn, ...txt2ImgMetaProps } = props;
  const baseMetaData = baseMetaFn(txt2ImgMetaProps);
  return {
    ...baseMetaData,
    url,
    fidelity,
  };
}

export function injectSDDefaultsTo(uri: string, meta: AIMetaType): void {
  const isImg2Img = uri === APIs_Img2Img.sd;
  meta.data.sampler ??= "k_euler";

  console.log("meta.data.num_steps: ", meta.data.num_steps);

  const model = meta.data.model;
  const defaultParams = model ? MODEL_META_PARAMS_CONFIG[model] : null;

  if (defaultParams) {
    const { num_steps, guidance_scale } = defaultParams;
    meta.data.num_steps ??= num_steps;
    meta.data.guidance_scale ??= guidance_scale;
  } else {
    meta.data.num_steps ??=
      meta.data.sampler === "k_euler"
        ? isImg2Img
          ? DEFAULT_NUM_STEPS + 5
          : DEFAULT_NUM_STEPS
        : meta.data.sampler === "solver"
        ? 25
        : 50;
    meta.data.guidance_scale ??= DEFAULT_GUIDANCE_SCALE;
  }

  if (meta.data.seed < 0) {
    meta.data.seed = getRandomSeed();
  }
}

export function parseModelType(meta: any): ModelTypes | false {
  if (isUndefined(meta)) return false;
  for (const model of THIRD_PARTY_MODEL_TYPES) {
    if (meta.type === model) {
      return model;
    }
  }
  const labels = parseLabels(meta);
  // 兼容写法
  if (labels.isAnime) return "anime";
  if (labels.version === DREAMLIKE_VERSION) {
    return "dreamlike";
  }

  if (labels.model) return META_MODAL_NOLI_MODEL_MAP[labels.model];
  if (!labels.isAI) return false;
  return "nolibox";
}
